import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useExpertsList } from '@/hooks/useLiveSessionsAudit';
import { useCreatePayout, useExpertMonthSessions } from '@/hooks/useExpertPayouts';

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function NewPayoutDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [month, setMonth] = useState(currentMonth());
  const [expertId, setExpertId] = useState<string>('');
  const [rate, setRate] = useState<string>('');
  const [notes, setNotes] = useState('');

  const { data: experts } = useExpertsList();
  const monthStart = month ? `${month}-01` : null;
  const { data: sessions = [], isLoading } = useExpertMonthSessions(expertId || null, monthStart);
  const create = useCreatePayout();

  const included = sessions.filter((s) => s.has_recording);
  const excluded = sessions.filter((s) => !s.has_recording);
  const totalHours = useMemo(() => included.reduce((s, x) => s + Number(x.duration_hours), 0), [included]);
  const rateNum = Number(rate) || 0;
  const total = totalHours * rateNum;

  const cohortIds = useMemo(
    () => Array.from(new Set(sessions.map((s: any) => s.cohort_id).filter(Boolean))),
    [sessions]
  );
  const { data: orgByCohort = {} } = useQuery({
    queryKey: ['payout-dialog-cohort-orgs', cohortIds],
    enabled: cohortIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cohorts')
        .select('id, organizations(name)')
        .in('id', cohortIds as string[]);
      if (error) throw error;
      const map: Record<string, string | null> = {};
      (data ?? []).forEach((c: any) => { map[c.id] = c.organizations?.name ?? null; });
      return map;
    },
  });

  const reset = () => { setExpertId(''); setRate(''); setNotes(''); setMonth(currentMonth()); };

  const submit = async () => {
    if (!expertId || !monthStart || rateNum <= 0) return;
    try {
      await create.mutateAsync({ expertId, month: monthStart, hourlyRate: rateNum, notes });
      reset();
      onOpenChange(false);
    } catch {}
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nueva liquidación</DialogTitle></DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Mes</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Experto</Label>
            <Select value={expertId} onValueChange={setExpertId}>
              <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>
                {experts?.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Valor hora (COP)</Label>
            <Input type="number" min={0} step={1000} value={rate} onChange={(e) => setRate(e.target.value)} placeholder="80000" />
          </div>
        </div>

        {expertId && (
          <div className="space-y-3 mt-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
              <div className="text-sm">
                <div><strong>{included.length}</strong> sesiones incluidas · <strong>{totalHours.toFixed(2)}h</strong></div>
                {excluded.length > 0 && <div className="text-xs text-warning">{excluded.length} excluidas por falta de grabación</div>}
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Total estimado</div>
                <div className="text-xl font-bold">{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(total)}</div>
              </div>
            </div>

            {isLoading && <div className="text-sm text-muted-foreground">Cargando sesiones…</div>}

            {included.length > 0 && (
              <div>
                <div className="text-xs font-semibold mb-1 text-success flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Incluidas</div>
                <ul className="text-sm space-y-1 max-h-40 overflow-y-auto">
                  {included.map((s: any) => (
                    <li key={s.schedule_item_id} className="flex justify-between gap-2 p-2 rounded bg-card border">
                      <span className="truncate">
                        {s.session_date} · {s.title} <Badge variant="outline" className="ml-1 text-xs">{s.session_type}</Badge>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {s.cohort_name ?? '—'}{(orgByCohort as any)[s.cohort_id] ? ` · ${(orgByCohort as any)[s.cohort_id]}` : ''}
                        </span>
                      </span>
                      <span className="font-mono whitespace-nowrap">{Number(s.duration_hours).toFixed(2)}h</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {excluded.length > 0 && (
              <div>
                <div className="text-xs font-semibold mb-1 text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Excluidas (sin grabación)</div>
                <ul className="text-xs space-y-1 max-h-32 overflow-y-auto opacity-70">
                  {excluded.map((s: any) => (
                    <li key={s.schedule_item_id} className="p-2 rounded bg-muted/50">
                      {s.session_date} · {s.title} · {Number(s.duration_hours).toFixed(2)}h
                      <span className="ml-2">{s.cohort_name ?? '—'}{(orgByCohort as any)[s.cohort_id] ? ` · ${(orgByCohort as any)[s.cohort_id]}` : ''}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <Label className="text-xs">Notas</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observaciones (opcional)" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={!expertId || rateNum <= 0 || create.isPending}>
            {create.isPending ? 'Creando…' : 'Crear liquidación'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
